import { 
  Shield, 
  Scale, 
  Building2, 
  Users, 
  BookOpen,
  Briefcase,
  Server,
  type LucideIcon 
} from "lucide-react";

// Content type key for UI (uses hyphens)
export type ContentTypeKey = 'qa' | 'company-content' | 'case-study' | 'team' | 'pricing';

// ============================================
// UNIVERSAL CATEGORIES (SIMPLIFIED TAXONOMY)
// ============================================

export interface UniversalCategoryDefinition {
  value: string;
  label: string;
  icon: LucideIcon;
  description: string;
}

// 6 Universal Categories - used across all content types
export const UNIVERSAL_CATEGORIES: UniversalCategoryDefinition[] = [
  { 
    value: 'security-compliance', 
    label: 'Security & Compliance',
    icon: Shield,
    description: 'Access control, certifications, data protection, audits',
  },
  { 
    value: 'delivery-operations', 
    label: 'Delivery & Operations',
    icon: Briefcase,
    description: 'Methodology, project management, support model, SLAs',
  },
  { 
    value: 'commercial-legal', 
    label: 'Commercial & Legal',
    icon: Scale,
    description: 'Pricing, payment terms, contracts, liability',
  },
  { 
    value: 'corporate-overview', 
    label: 'Corporate Overview',
    icon: Building2,
    description: 'Company history, financials, governance, sustainability',
  },
  { 
    value: 'people-expertise', 
    label: 'People & Expertise',
    icon: Users,
    description: 'Team bios, qualifications, capacity, experience',
  },
  { 
    value: 'case-studies-evidence', 
    label: 'Case Studies & Evidence',
    icon: BookOpen,
    description: 'Client success stories, project examples',
  },
  { 
    value: 'technology-infrastructure', 
    label: 'Technology & Infrastructure',
    icon: Server,
    description: 'Architecture, platforms, integrations, hosting, tools',
  },
];

// Allowed categories per content type
export const ALLOWED_CATEGORIES_BY_TYPE: Record<ContentTypeKey, string[]> = {
  'team': ['people-expertise'],
  'case-study': ['case-studies-evidence'],
  'pricing': ['commercial-legal'],
  'qa': UNIVERSAL_CATEGORIES.map(c => c.value),
  'company-content': ['security-compliance', 'delivery-operations', 'commercial-legal', 'corporate-overview', 'technology-infrastructure'],
};

// Get filtered categories for a content type
export function getAllowedCategories(type?: ContentTypeKey): UniversalCategoryDefinition[] {
  if (!type) return UNIVERSAL_CATEGORIES;
  const allowed = ALLOWED_CATEGORIES_BY_TYPE[type];
  return UNIVERSAL_CATEGORIES.filter(c => allowed.includes(c.value));
}

// Helper to get category by value
export function getUniversalCategory(value: string): UniversalCategoryDefinition | undefined {
  return UNIVERSAL_CATEGORIES.find(c => c.value === value);
}

// Helper to get category label
export function getUniversalCategoryLabel(value: string): string {
  return getUniversalCategory(value)?.label || value;
}

// Muted category pills — tinted background + colored text. Keeps the colour
// association from the previous solid pills but reduces saturation so they
// don't compete with row content. Identical in light and dark mode.
export function getCategoryPillStyle(value: string): { background: string; color: string } {
  const map: Record<string, string> = {
    'security-compliance':       '#dc2626',
    'delivery-operations':       '#2563eb',
    'commercial-legal':          '#d97706',
    'corporate-overview':        '#0d9488',
    'people-expertise':          '#16a34a',
    'case-studies-evidence':     '#9333ea',
    'technology-infrastructure': '#4f46e5',
  };
  const color = map[value] ?? '#6b7280';
  // 8-digit hex: ~12% alpha background tint, full-strength text in the same hue.
  return { background: `${color}1f`, color };
}



// Infer content type from selected category
export function categoryToContentType(category: string): ContentTypeKey {
  switch (category) {
    case 'people-expertise': return 'team';
    case 'case-studies-evidence': return 'case-study';
    default: return 'qa';
  }
}

// Map content type to suggested default category
export function contentTypeToDefaultCategory(type: ContentTypeKey): string {
  switch (type) {
    case 'case-study':
      return 'case-studies-evidence';
    case 'team':
      return 'people-expertise';
    case 'pricing':
      return 'commercial-legal';
    default:
      return 'corporate-overview';
  }
}

// Category definition for content types
export interface CategoryDefinition {
  value: ContentTypeKey;
  label: string;
  description: string;
  isProtected: boolean;
}

// PERMANENT CATEGORIES - Cannot be deleted (used for content type detection)
export const PERMANENT_CATEGORIES: CategoryDefinition[] = [
  { value: 'qa', label: 'Q&A', description: 'Question and answer pairs', isProtected: true },
  { value: 'case-study', label: 'Case Studies', description: 'Client success stories', isProtected: true },
  { value: 'company-content', label: 'Company Content', description: 'Core company narratives used across proposals', isProtected: true },
  { value: 'team', label: 'Team', description: 'Team member profiles', isProtected: true },
  { value: 'pricing', label: 'Pricing Templates', description: 'Pricing and commercial templates', isProtected: true },
];

// Get category by value
export function getCategory(value: ContentTypeKey): CategoryDefinition | undefined {
  return PERMANENT_CATEGORIES.find(c => c.value === value);
}

// Get category label
export function getCategoryLabel(value: string): string {
  const category = PERMANENT_CATEGORIES.find(c => c.value === value);
  return category?.label ?? value;
}

